import { useTranslation } from 'react-i18next';
import { ArrowLeft, MoreSmall } from '@peacecover/icon';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';
import { useBettieProfilePictureActions } from '../hooks/useBettieProfilePictureActions';
import { MenuDropdown, MenuDropdownItem } from '../../components/popup/MenuDropdown';
import type { components } from '@pocapan/shared/types/schema';

type ChatRoomUserPicture = components['schemas']['ApiV1BettieChatRoomUserPicture'];

interface BettieChangeProfileHeaderProps {
  bettieNickname: string;
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isMenuOpen: boolean;
  currentProfilePicture: ChatRoomUserPicture | null;
  chatRoomId: string;
}

const BettieChangeProfileHeader = ({
  bettieNickname,
  setIsMenuOpen,
  isMenuOpen,
  currentProfilePicture,
  chatRoomId
}: BettieChangeProfileHeaderProps) => {
  const { t } = useTranslation();
  const handleHistoryBack = useHandleHistoryBack({
    fallbackPath: `/bettie-chats/${chatRoomId}`,
    nativeCloseFallbackDelayMs: 500,
    preferNativeWebViewClose: true
  });
  const { handleDeleteProfilePicture, handleToggleBettieProfileVisibility } =
    useBettieProfilePictureActions({ chatRoomId });

  const handleMenuClick = () => {
    if (!isCurrentProfile) return;
    setIsMenuOpen((prev) => !prev);
  };

  const isCurrentProfile = currentProfilePicture?.type === 'current';
  const isVisible = currentProfilePicture?.visibility ?? false;
  const hasCurrentProfile = currentProfilePicture?.pictureUrl !== null;

  return (
    <header className="flex items-center justify-between px-[16px] py-[8px]">
      <button
        type="button"
        aria-label="뒤로가기"
        className="flex h-[40px] w-[40px] touch-manipulation items-center justify-center"
        onClick={handleHistoryBack}
      >
        <ArrowLeft size={24} className="text-text-main-lv1" />
      </button>
      <p className="text-[17px] font-bold leading-[20px] text-text-main-lv1">{bettieNickname}</p>
      <div className="relative">
        <button
          type="button"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={handleMenuClick}
          disabled={!isCurrentProfile}
        >
          <MoreSmall size={24} className="text-text-main-lv1" />
        </button>
        {isCurrentProfile && isMenuOpen && hasCurrentProfile && (
          <MenuDropdown
            className="absolute bottom-[-100px] right-[10px]"
            onClickOutside={() => setIsMenuOpen(false)}
          >
            <MenuDropdownItem
              className="!text-left whitespace-nowrap"
              onClick={() => {
                handleToggleBettieProfileVisibility(!isVisible);
                setIsMenuOpen(false);
              }}
            >
              {isVisible ? t('BettieChangeProfile.onlyShowMe') : t('BettieChangeProfile.showToAll')}
            </MenuDropdownItem>
            <MenuDropdownItem
              className="!text-left whitespace-nowrap"
              onClick={() => {
                handleDeleteProfilePicture();
                setIsMenuOpen(false);
              }}
            >
              {t('BettieChangeProfile.deleteProfile')}
            </MenuDropdownItem>
          </MenuDropdown>
        )}
      </div>
    </header>
  );
};

export default BettieChangeProfileHeader;
